import { FormattedMessage, useIntl } from 'react-intl';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableBody from '@mui/material/TableBody';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import Typography from '@mui/material/Typography';
import { formatDate } from '../../utils/formatDate';
import { netMinutes, formatHoursMinutes } from './timeMath';
import OpenEntryButton from './OpenEntryButton';

/**
 * The entries behind a double booking, one per row — the detail panel of a
 * `doubleBooking` finding in the Abgleich, and of a conflicting row in the
 * Zeiterfassung itself.
 *
 * Deliberately NOT the two-column comparison the Abgleich's other findings get:
 * there is no Crewmeister side here. Every row is ours, and what has to be
 * readable is which of them to correct — so they are listed under one another,
 * in the order the day ran, with the times first.
 *
 * The heading and the sentence under the table are passed in: each home says it
 * in its own words.
 */
/** An entry as this table reads it — a time entry or a reconcile entry ref. */
export interface ConflictEntryRow {
    id: string;
    /** Only read when the table spans more than one day (`showDate`). */
    date?: string;
    startTime: string;
    endTime: string;
    pauseMinutes: number;
    note: string;
    /** The Maßnahme label; both callers resolve the project id themselves. */
    project: string;
}

export interface DoubleBookingDetailProps {
    entries: ConflictEntryRow[];
    /** i18n id for the heading above the table. */
    titleId: string;
    /** i18n id for the sentence under it, saying what to do about it. */
    helpId: string;
    /**
     * The entry the panel was opened FROM, if it is among them: it is marked as
     * "this one" and gets no open button — you are already there.
     */
    currentId?: string;
    /** Open one of the others for correction (Abgleich: in place). */
    onOpen?: (entryId: string) => void;
    /** The route of an entry, when opening it means navigating (Zeiterfassung). */
    entryPath?: (entryId: string) => string;
    /**
     * Lead with a Datum column. Off by default: a double booking is by
     * definition one day. The ArbZG hints are the exception — a short rest is
     * precisely about two days.
     */
    showDate?: boolean;
}

export default function DoubleBookingDetail({
    entries,
    titleId,
    helpId,
    currentId,
    onOpen,
    entryPath,
    showDate = false
}: DoubleBookingDetailProps) {
    const intl = useIntl();
    if (!entries.length) {
        return null;
    }

    return (
        <Paper
            variant="outlined"
            sx={{ m: 1, p: 2, backgroundColor: 'action.hover' }}
        >
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
                <FormattedMessage id={titleId} />
            </Typography>
            <Table size="small" sx={{ maxWidth: 860 }}>
                <TableHead>
                    <TableRow>
                        {showDate && (
                            <TableCell sx={{ width: 120 }}>
                                <FormattedMessage id="timeEntry.field.date" />
                            </TableCell>
                        )}
                        <TableCell sx={{ width: 90 }}>
                            <FormattedMessage id="timeEntry.field.startTime" />
                        </TableCell>
                        <TableCell sx={{ width: 90 }}>
                            <FormattedMessage id="timeEntry.field.endTime" />
                        </TableCell>
                        <TableCell sx={{ width: 90 }}>
                            <FormattedMessage id="reconcile.compare.pause" />
                        </TableCell>
                        <TableCell sx={{ width: 90 }}>
                            <FormattedMessage id="reconcile.compare.net" />
                        </TableCell>
                        <TableCell sx={{ width: 220 }}>
                            <FormattedMessage id="timeEntry.field.projectId" />
                        </TableCell>
                        <TableCell>
                            <FormattedMessage id="timeEntry.field.note" />
                        </TableCell>
                        <TableCell sx={{ width: 96 }} />
                    </TableRow>
                </TableHead>
                <TableBody>
                    {entries.map((entry) => (
                        <TableRow
                            key={entry.id}
                            // The row you came from is context, not a candidate
                            // to go to — dimmed so the OTHER bookings carry the
                            // weight.
                            sx={
                                entry.id === currentId
                                    ? { opacity: 0.7 }
                                    : undefined
                            }
                        >
                            {showDate && (
                                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                    {formatDate(entry.date)}
                                </TableCell>
                            )}
                            {/* The times are what overlaps, so they carry the
                                emphasis — the rest is here to tell the two rows
                                apart. */}
                            <TableCell sx={{ fontWeight: 'bold' }}>
                                {entry.startTime}
                            </TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>
                                {entry.endTime}
                            </TableCell>
                            <TableCell>
                                {intl.formatMessage(
                                    { id: 'reconcile.compare.minutes' },
                                    { minutes: entry.pauseMinutes }
                                )}
                            </TableCell>
                            <TableCell>
                                {formatHoursMinutes(
                                    netMinutes(
                                        entry.startTime,
                                        entry.endTime,
                                        entry.pauseMinutes
                                    )
                                )}
                            </TableCell>
                            <TableCell>{entry.project || '—'}</TableCell>
                            <TableCell
                                sx={{
                                    color: 'text.secondary',
                                    verticalAlign: 'top'
                                }}
                            >
                                {entry.note || '—'}
                            </TableCell>
                            <TableCell align="right">
                                {entry.id === currentId ? (
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{ whiteSpace: 'nowrap' }}
                                    >
                                        <FormattedMessage id="timeEntry.conflict.thisEntry" />
                                    </Typography>
                                ) : (
                                    // Neither way to open one given (the
                                    // read-only conflict dialog): no button at
                                    // all rather than one that does nothing.
                                    (!!entryPath || !!onOpen) && (
                                        <OpenEntryButton
                                            to={entryPath?.(entry.id)}
                                            onClick={
                                                onOpen
                                                    ? () => onOpen(entry.id)
                                                    : undefined
                                            }
                                        />
                                    )
                                )}
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
            <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', mt: 1 }}
            >
                <FormattedMessage id={helpId} />
            </Typography>
        </Paper>
    );
}
